import { yupResolver } from "@hookform/resolvers/yup";
import { DEFAULT_SECTION_MARGINS, SectionMargins, ContactLocationsSectionContent } from "api/shop";
import { PaziaFormButton, PaziaInput, PaziaSelect } from "components/atoms";
import { FormProvider, useFieldArray, useForm, useFormContext } from "react-hook-form";
import { MdAdd, MdDelete, MdClose } from "react-icons/md";
import * as yup from "yup";
import { ANIMATION_OPTIONS } from "./animationOptions";
import { MARGINS_FORM_NAME, marginsSchema, SectionMarginsConfig } from "./SectionMarginsConfig";

const FORM_NAMES = {
  ANIMATION: "animation",
  LOCATIONS: "locations",
} as const;

const detailSchema = yup.object({
  label: yup.string().required("Etykieta jest wymagana"),
  value: yup.string().required("Wartość jest wymagana"),
});

const addressLineSchema = yup.object({
  value: yup.string().required("Linia adresu jest wymagana"),
});

const TITLE_TAG_OPTIONS = [
  { value: "h1", label: "h1" },
  { value: "h2", label: "h2" },
  { value: "h3", label: "h3" },
  { value: "h4", label: "h4" },
  { value: "h5", label: "h5" },
  { value: "p", label: "p" },
  { value: "span", label: "span" },
];

const locationSchema = yup.object({
  title: yup.string().required("Tytuł jest wymagany"),
  titleTag: yup.string().default("h3"),
  addressLines: yup.array().of(addressLineSchema).default([]),
  details: yup.array().of(detailSchema).default([]),
});

const schema = yup
  .object({
    [FORM_NAMES.ANIMATION]: yup.string().required("Animacja jest wymagana"),
    [FORM_NAMES.LOCATIONS]: yup.array().of(locationSchema).min(1, "Dodaj przynajmniej jedną lokalizację"),
    [MARGINS_FORM_NAME]: marginsSchema,
  })
  .required();

interface FormAddressLine {
  value: string;
}

interface FormDetail {
  label: string;
  value: string;
}

interface FormLocation {
  title: string;
  titleTag: string;
  addressLines: FormAddressLine[];
  details: FormDetail[];
}

interface FormValues {
  animation: string;
  locations: FormLocation[];
  margins: SectionMargins;
}

export interface ContactLocationsFormValues {
  animation: string;
  locations: Array<{
    title: string;
    titleTag?: string;
    addressLines: string[];
    details: Array<{ label: string; value: string }>;
  }>;
  margins?: SectionMargins;
}

interface ContactLocationsFormProps {
  onSubmit: (data: ContactLocationsFormValues) => void;
  onCancel: () => void;
  defaultValues?: ContactLocationsSectionContent;
  isLoading?: boolean;
}

function LocationCard({ index, remove }: { index: number; remove: (i: number) => void }) {
  const { control } = useFormContext<FormValues>();

  const {
    fields: addressFields,
    append: appendAddress,
    remove: removeAddress,
  } = useFieldArray({
    control,
    name: `locations.${index}.addressLines` as const,
  });

  const {
    fields: detailFields,
    append: appendDetail,
    remove: removeDetail,
  } = useFieldArray({
    control,
    name: `locations.${index}.details` as const,
  });

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-600 dark:bg-slate-800">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-600 dark:text-slate-400">
          Lokalizacja #{index + 1}
        </span>
        <button
          type="button"
          onClick={() => remove(index)}
          className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
          title="Usuń lokalizację"
        >
          <MdDelete size={18} />
        </button>
      </div>

      <div className="grid gap-3">
        <PaziaInput
          name={`${FORM_NAMES.LOCATIONS}.${index}.title`}
          label="Tytuł lokalizacji"
          required
        />
        <PaziaSelect
          name={`${FORM_NAMES.LOCATIONS}.${index}.titleTag`}
          label="Poziom nagłówka (SEO)"
          options={TITLE_TAG_OPTIONS}
        />

        {/* Address Lines */}
        <div className="rounded-md border border-slate-200 p-3 dark:border-slate-600">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Linie adresu</span>
            <button
              type="button"
              onClick={() => appendAddress({ value: "" })}
              className="flex items-center gap-1 rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={14} />
              Dodaj linię
            </button>
          </div>
          {addressFields.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">Brak linii adresu.</p>
          ) : (
            <div className="space-y-2">
              {addressFields.map((field, addrIndex) => (
                <div key={field.id} className="flex items-center gap-2">
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.LOCATIONS}.${index}.addressLines.${addrIndex}.value`}
                      placeholder="np. ul. Królewska 65A/1"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAddress(addrIndex)}
                    className="mt-1 rounded p-1 text-slate-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń linię"
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div className="rounded-md border border-slate-200 p-3 dark:border-slate-600">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Szczegóły</span>
            <button
              type="button"
              onClick={() => appendDetail({ label: "", value: "" })}
              className="flex items-center gap-1 rounded bg-indigo-100 px-2 py-1 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={14} />
              Dodaj szczegół
            </button>
          </div>
          {detailFields.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">Brak szczegółów.</p>
          ) : (
            <div className="space-y-2">
              {detailFields.map((field, detailIndex) => (
                <div key={field.id} className="flex items-center gap-2">
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.LOCATIONS}.${index}.details.${detailIndex}.label`}
                      placeholder="Etykieta (np. NIP)"
                    />
                  </div>
                  <div className="flex-1">
                    <PaziaInput
                      name={`${FORM_NAMES.LOCATIONS}.${index}.details.${detailIndex}.value`}
                      placeholder="Wartość"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDetail(detailIndex)}
                    className="mt-1 rounded p-1 text-slate-400 transition-colors hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/30 dark:hover:text-red-400"
                    title="Usuń szczegół"
                  >
                    <MdClose size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const ContactLocationsForm = ({ onSubmit, onCancel, defaultValues, isLoading }: ContactLocationsFormProps) => {
  const methods = useForm<FormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      [FORM_NAMES.ANIMATION]: defaultValues?.animation ?? "fade-bottom",
      [FORM_NAMES.LOCATIONS]: defaultValues?.locations?.map((loc) => ({
        title: loc.title,
        titleTag: loc.titleTag ?? "h3",
        addressLines: loc.addressLines?.map((line) => ({ value: line })) ?? [],
        details: loc.details ?? [],
      })) ?? [],
      [MARGINS_FORM_NAME]: (defaultValues as any)?.margins ?? DEFAULT_SECTION_MARGINS,
    },
  });
  const { handleSubmit, control } = methods;

  const {
    fields: locationFields,
    append: appendLocation,
    remove: removeLocation,
  } = useFieldArray({
    control,
    name: FORM_NAMES.LOCATIONS,
  });

  const handleFormSubmit = async (data: FormValues) => {
    const formattedData: ContactLocationsFormValues = {
      animation: data.animation,
      locations: data.locations.map((loc) => ({
        title: loc.title,
        titleTag: loc.titleTag,
        addressLines: loc.addressLines.map((line) => line.value),
        details: loc.details,
      })),
      margins: data.margins,
    };
    onSubmit(formattedData);
  };

  const addLocation = () => {
    appendLocation({ title: "", titleTag: "h3", addressLines: [], details: [] });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="grid gap-4">
        <PaziaSelect name={FORM_NAMES.ANIMATION} label="Animacja" required options={ANIMATION_OPTIONS} />

        {/* Locations Section */}
        <div className="border-t border-slate-200 pt-4 dark:border-slate-600">
          <div className="mb-3 flex items-center justify-between">
            <h4 className="font-medium text-slate-700 dark:text-slate-300">Lokalizacje</h4>
            <button
              type="button"
              onClick={addLocation}
              className="flex items-center gap-1 rounded-lg bg-indigo-100 px-3 py-1.5 text-sm font-medium text-indigo-600 transition-colors hover:bg-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:hover:bg-indigo-900/50"
            >
              <MdAdd size={18} />
              Dodaj lokalizację
            </button>
          </div>

          {locationFields.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Brak lokalizacji. Kliknij "Dodaj lokalizację", aby dodać.
            </p>
          ) : (
            <div className="space-y-4">
              {locationFields.map((field, index) => (
                <LocationCard key={field.id} index={index} remove={removeLocation} />
              ))}
            </div>
          )}
        </div>

        <SectionMarginsConfig />

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-600">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-slate-300 px-4 py-2 text-slate-700 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
          >
            Anuluj
          </button>
          <PaziaFormButton disabled={isLoading} />
        </div>
      </form>
    </FormProvider>
  );
};
